import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";

type Account = components["schemas"]["Account"];
type AccountListResponse = components["schemas"]["AccountListResponse"];
type AccountCreate = components["schemas"]["AccountCreate"];
type AccountType = components["schemas"]["AccountType"];
type AccountDeleteConflictError = components["schemas"]["AccountDeleteConflictError"];

// Accounts are the correspondence parties premises and assemblies are
// associated with. Per spec §Frontend Caching + Routing Performance Contract:
// list-hook staleTime 60s
// (short stale for fresh writes), gcTime 5min, keepPreviousData for smooth
// pagination; detail-hook staleTime 2min, gcTime 10min (detail navigation
// returns shouldn't refetch).
const LIST_STALE_TIME = 60_000;
const LIST_GC_TIME = 5 * 60_000;
const DETAIL_STALE_TIME = 2 * 60_000;
const DETAIL_GC_TIME = 10 * 60_000;

export type AccountSortBy =
  | "name"
  | "account_number"
  | "account_type"
  | "preferred_notice_channel"
  | "created_at"
  | "updated_at"
  | "deleted_at";

export type AccountStatusFilter = "active" | "archived" | "all";

export interface AccountListParams {
  name_search?: string;
  account_type?: AccountType;
  /**
   * Spec §Hook Surface Contract uses the canonical `deleted_filter` token in
   * the hook surface; the OpenAPI operation parameter is named `status`. The
   * hook accepts the spec-canonical alias AND forwards it to the URL as the
   * generated query-param key.
   */
  deleted_filter?: AccountStatusFilter;
  limit?: number;
  offset?: number;
  sort_by?: AccountSortBy;
  sort_dir?: "asc" | "desc";
}

/**
 * Query-key factory for account caches. Shared with admin parity hooks
 * via the future `use-admin-accounts.ts` import.
 */
export const accountKeys = {
  all: ["accounts"] as const,
  lists: () => [...accountKeys.all, "list"] as const,
  list: (params: AccountListParams) => [...accountKeys.lists(), params] as const,
  /**
   * Where a DISABLED list query parks. A disabled caller's params default to
   * the same `{limit, offset}` an enabled empty-params caller sends, so without
   * a key of its own the two share one cache entry — and removing the other
   * caller's guard would serve it rows it never asked for.
   */
  disabledList: () => [...accountKeys.lists(), "disabled"] as const,
  details: () => [...accountKeys.all, "detail"] as const,
  detail: (id: string) => [...accountKeys.details(), id] as const,
} as const;

function withDefaults(params: AccountListParams): AccountListParams {
  // Spec §Hook Surface Contract row `useAccounts`: default limit=50 (matches
  // OpenAPI listAccounts default).
  return {
    ...params,
    limit: params.limit ?? 20,
    offset: params.offset ?? 0,
  };
}

/**
 * List account rows. Pass `null` to disable the query — a caller whose search
 * term is not typed yet would otherwise page the org's whole account list.
 */
export function useAccounts(params: AccountListParams | null = {}) {
  const enabled = params !== null;
  const merged = withDefaults(params ?? {});
  // The spec uses `deleted_filter` as the canonical hook param name but the
  // OpenAPI operation parameter is named `status`. Translate at the wire
  // boundary; the URL receives `status=<token>`.
  const { deleted_filter, ...rest } = merged;
  const query = {
    ...rest,
    ...(deleted_filter !== undefined ? { status: deleted_filter } : {}),
  };
  return useQuery<AccountListResponse>({
    queryKey: enabled ? accountKeys.list(merged) : accountKeys.disabledList(),
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET("/accounts", {
        params: { query },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to list accounts";
        throw new ApiError(response.status, msg);
      }
      return data;
    },
    enabled,
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AccountCreate) => {
      const { data, error, response } = await apiClient.POST("/accounts", {
        body,
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to create account";
        // The 409 AccountNumberConflictError envelope carries `code:
        // "account_number_conflict"` — threaded through so the create route
        // can surface it as a field-level error on account_number instead of
        // a generic banner.
        throw new ApiError(response.status, msg, (error as { code?: string }).code);
      }
      return data as Account;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
    },
  });
}

/**
 * useDeleteAccount soft-deletes an account row. Its 409 envelope is a single
 * typed shape discriminated by `code` per spec §Error Envelopes: the dependent
 * meters arm carries `meter_count` / `meter_ids` /
 * `reassign_account_url`, the facility-association arm `association_count` /
 * `facility_ids`, the soft-delete-immutable arm `deleted_at`. The typed payload
 * is threaded through `ApiError.details` (NOT collapsed to a `.message` string)
 * so the modal can render concrete next-step affordances.
 *
 * Re-exported from `use-account.ts` too for callsite ergonomics; the
 * single source of truth lives here.
 */
export { DETAIL_STALE_TIME, DETAIL_GC_TIME };

export type { AccountListResponse, AccountDeleteConflictError };
